/**
 * Tabbed layout: a horizontally scrollable tab bar over a scrollable content
 * panel that renders the active tab's render() function. The active tab is
 * synced with the "?tab=" query param (slugified tab name), initialized from
 * the URL before first render and updated via history.replaceState on change.
 * Used by the admin pages (bounties, submissions, users, AI search, and
 * submission-verification).
 */
import React, { useState, useEffect } from "react";

interface Tab {
    tabName: string;
    render: () => React.ReactNode;
}

interface TabbedPageProps {
    tabs: Tab[];
}

const TabbedPage: React.FC<TabbedPageProps> = ({ tabs }) => {
    // Initialize the active tab from the URL BEFORE first render to avoid mounting the wrong tab
    const [activeTab, setActiveTab] = useState<number>(() => {
        try {
            const urlParams = new URLSearchParams(window.location.search);
            const tabParam = urlParams.get("tab");
            if (tabParam) {
                const tabIndex = tabs.findIndex(
                    (tab) => tab.tabName.toLowerCase().replace(/\s+/g, "-") === tabParam
                );
                if (tabIndex !== -1) {
                    return tabIndex;
                }
            }
        } catch (_e) {
            // No-op for environments where window is not available
        }
        return 0;
    });

    // If the tabs array changes (rare), ensure the active index still points to the intended tab
    useEffect(() => {
        // Guard against non-browser environments and SSR
        if (typeof window === "undefined") return;

        // If tabs shrank or were reordered, ensure we don't point past the end
        if (activeTab >= tabs.length) {
            setActiveTab(Math.max(0, tabs.length - 1));
            return;
        }

        const urlParams = new URLSearchParams(window.location.search);
        const tabParam = urlParams.get("tab");
        if (tabParam) {
            const tabIndex = tabs.findIndex(
                (tab) => tab.tabName.toLowerCase().replace(/\s+/g, "-") === tabParam
            );
            if (tabIndex !== -1 && tabIndex !== activeTab) {
                setActiveTab(tabIndex);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabs]);

    // Update URL when tab changes
    const handleTabChange = (index: number) => {
        setActiveTab(index);
        const tabName = tabs[index].tabName.toLowerCase().replace(/\s+/g, "-");
        const url = new URL(window.location.href);
        url.searchParams.set("tab", tabName);
        window.history.replaceState({}, "", url.toString());
    };

    return (
        <div className="h-full w-full flex flex-col bg-slate-50 relative p-4 shadow-md">
            {/* Tab Bar - Horizontally Scrollable */}
            <div className="flex-shrink-0 w-full flex text-black font-porsui z-10 overflow-x-auto whitespace-nowrap pb-2">
                {tabs.map((tab, index) => (
                    <button
                        key={index}
                        onClick={() => handleTabChange(index)}
                        className={`px-4 py-2 flex-shrink-0 ${
                            activeTab === index ? "bg-white" : "bg-gray-100"
                        } rounded-md mx-1`}
                    >
                        {tab.tabName}
                    </button>
                ))}
            </div>
            {/* Content Panel - Vertically Scrollable */}
            <div className="bg-white shadow-md rounded-md p-4 flex-1 h-full overflow-y-auto">
                {tabs[activeTab].render()}
            </div>
        </div>
    );
};

export default TabbedPage;
